import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GraduationCap, MapPin, Phone, Mail, Star, MessageSquare, Video, DollarSign, Book, Languages, Coins, ShieldCheck, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { api, setStoredUser } from '@/utils/api';
import { trackContact, trackViewContent } from '@/lib/metaPixel';

const extractYouTubeVideoId = (rawUrl = '') => {
  const url = String(rawUrl || '').trim();
  if (!url) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') return parsed.pathname.replace('/', '').split('/')[0] || '';
    if (host.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') return parsed.searchParams.get('v') || '';
      if (parsed.pathname.startsWith('/embed/')) return parsed.pathname.split('/embed/')[1]?.split('/')[0] || '';
      if (parsed.pathname.startsWith('/shorts/')) return parsed.pathname.split('/shorts/')[1]?.split('/')[0] || '';
    }
  } catch (_err) {
    return '';
  }
  return '';
};

const getYouTubeEmbedUrl = (rawUrl = '') => {
  const id = extractYouTubeVideoId(rawUrl);
  if (!id) return '';
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    iv_load_policy: '3',
    playsinline: '1',
    loop: '1',
    playlist: id
  });
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
};

const getQualificationItems = (education = {}) => {
  const levels = [
    { key: 'tenth', label: '10th' },
    { key: 'twelfth', label: '12th' },
    { key: 'graduation', label: 'Graduation' },
    { key: 'postgraduation', label: 'Post Graduation' },
    { key: 'phd', label: 'PhD' },
    { key: 'diploma', label: 'Diploma' }
  ];

  return levels
    .map(({ key, label }) => {
      const row = education?.[key];
      if (!row || typeof row !== 'object') return null;
      const details = Object.values(row)
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      if (!details.length) return null;
      return { label, details: details.join(' • ') };
    })
    .filter(Boolean);
};

const formatResponseTime = (minutes) => {
  if (minutes === null || minutes === undefined) return 'Not enough data yet';
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins < 0) return 'Not enough data yet';
  if (mins < 60) return `${Math.round(mins)} min average`;
  const hrs = mins / 60;
  if (hrs < 24) return `${hrs.toFixed(1)} hr average`;
  return `${(hrs / 24).toFixed(1)} day average`;
};

export default function TutorProfile({ user }) {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [accessInfo, setAccessInfo] = useState({ has_message_access: false, has_contact_access: false, current_coins: 0 });
  const [unlockedPhone, setUnlockedPhone] = useState('');

  const [existingReview, setExistingReview] = useState(null);

  useEffect(() => {
    loadTutorProfile();
    loadReviews();
    trackProfileView();
  }, [tutorId]);

  useEffect(() => {
    if (user) {
      checkAccess();
      checkExistingReview();
    }
  }, [tutorId, user]);

  useEffect(() => {
    if (accessInfo.has_contact_access && tutor?.mobile && !unlockedPhone) {
      setUnlockedPhone(tutor.mobile);
    }
  }, [accessInfo.has_contact_access, tutor, unlockedPhone]);

  useEffect(() => {
    if (!tutor) return;
    trackViewContent({
      content_name: tutor.name || 'Tutor Profile',
      content_category: 'Tutor Profile',
      content_ids: [tutorId],
      contents: [{ id: tutorId, quantity: 1 }],
      location: tutor.location,
      subject_count: Array.isArray(tutor.subjects) ? tutor.subjects.length : 0,
    }, {
      external_id: user?.id,
      email: user?.email,
      mobile: user?.mobile,
    });
  }, [tutor, tutorId, user]);

  const loadTutorProfile = async () => {
    try {
      const response = await api.get(`/tutors/${tutorId}`);
      setTutor(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load tutor profile:', error);
      toast.error('Failed to load tutor profile');
      setLoading(false);
    }
  };

  const trackProfileView = async () => {
    try {
      await api.post(`/tutors/${tutorId}/view`);
    } catch (error) {
      // Silent fail - don't interrupt user experience
    }
  };

  const loadReviews = async () => {
    try {
      const response = await api.get(`/reviews/${tutorId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const checkExistingReview = async () => {
    try {
      const response = await api.get(`/reviews/check/${tutorId}`);
      if (response.data.has_review) {
        setExistingReview(response.data.review);
        setReviewData({
          rating: response.data.review.rating,
          comment: response.data.review.comment
        });
      }
    } catch (error) {
      console.error('Failed to check existing review:', error);
    }
  };

  const checkAccess = async () => {
    try {
      const response = await api.get(`/check-tutor-access/${tutorId}`);
      setAccessInfo(response.data);
      if (response.data?.has_contact_access && tutor?.mobile) {
        setUnlockedPhone(tutor.mobile);
      }
    } catch (error) {
      console.error('Failed to check access:', error);
    }
  };

  const handleContactTutor = async () => {
    if (!user) {
      toast.error('Please login to contact tutors');
      navigate('/login');
      return;
    }

    if ((accessInfo.current_coins || user?.coins || 0) < 100) {
      toast.error('Insufficient coins. You need 100 coins to contact this tutor.');
      return;
    }
    
    try {
      const response = await api.post('/wallet/spend', null, {
        params: { coins: 100, purpose: 'contact_tutor', target_id: tutorId }
      });
      
      toast.success('Contact information unlocked!');
      trackContact({
        content_name: 'student_contact_tutor',
        content_category: 'Tutor Contact Unlock',
        content_ids: [tutorId],
        value: 100,
        currency: 'INR',
        tutor_name: tutor?.name,
      }, {
        external_id: user?.id,
        email: user?.email,
        mobile: user?.mobile,
      });
      if (response.data.data) {
        setUnlockedPhone(response.data.data.mobile || tutor?.mobile || '');
      }
      if (typeof response.data.remaining_coins === 'number' && user) {
        setStoredUser({ ...user, coins: response.data.remaining_coins });
      }
      setAccessInfo((prev) => ({
        ...prev,
        has_contact_access: true,
        has_message_access: true,
        current_coins: typeof response.data.remaining_coins === 'number'
          ? response.data.remaining_coins
          : Math.max(0, (prev.current_coins || user?.coins || 0) - 100)
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      toast.error('Please login to send messages');
      navigate('/login');
      return;
    }
    
    // Check if user has already paid to message this tutor
    if (!accessInfo.has_message_access && user.role !== 'tutor') {
      if (accessInfo.current_coins < 100) {
        toast.error('Insufficient coins. You need 100 coins to message this tutor.');
        navigate('/wallet');
        return;
      }
      // Coins will be deducted when the first message is sent
      toast.info('100 coins will be deducted when you send your first message to this tutor.');
    }
    
    trackContact({
      content_name: 'student_message_tutor',
      content_category: 'Tutor Messaging',
      content_ids: [tutorId],
      tutor_name: tutor?.name,
    }, {
      external_id: user?.id,
      email: user?.email,
      mobile: user?.mobile,
    });
    navigate('/messages', { state: { recipientId: tutorId, recipientName: tutor?.name } });
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      navigate('/login');
      return;
    }

    try {
      const response = await api.post('/reviews', {
        tutor_id: tutorId,
        rating: reviewData.rating,
        comment: reviewData.comment
      });
      
      if (response.data.updated) {
        toast.success('Review updated successfully!');
      } else {
        toast.success('Review submitted successfully!');
      }
      
      setShowReviewModal(false);
      setExistingReview({ ...reviewData });
      loadReviews();
      loadTutorProfile();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!tutor) {
    return <div className="min-h-screen flex items-center justify-center">Tutor not found</div>;
  }

  const qualificationItems = getQualificationItems(tutor.education);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-outfit font-bold">Tricity Tutors</span>
          </Link>
          <div className="flex items-center gap-4">
            {user && (
              <Link to="/wallet">
                <Button data-testid="profile-wallet-btn" variant="outline" className="rounded-full flex items-center gap-2">
                  <Coins className="w-4 h-4 text-yellow-600" />
                  <span className="font-semibold">{accessInfo.current_coins || user?.coins || 0}</span>
                </Button>
              </Link>
            )}
            <Link to="/tutors">
              <Button data-testid="profile-back-btn" variant="outline" className="rounded-full">
                Back to Browse
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Section */}
            <Card className="overflow-hidden">
              {/* Cover Image */}
              <div className="h-32 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
              <CardContent className="pt-0 relative">
                <div className="flex items-start gap-6 -mt-12">
                  <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                    <AvatarImage src={tutor.profile_photo} alt={tutor.name} />
                    <AvatarFallback className="text-3xl font-bold bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                      {tutor.name?.charAt(0) || 'T'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 pt-14">
                    <h1 className="text-3xl font-outfit font-bold mb-2">{tutor.name}</h1>
                    <div className="flex items-center gap-2 text-muted-foreground mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{tutor.location || 'Location not set'}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= Math.round(tutor.average_rating || 0)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold">{tutor.average_rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-sm text-muted-foreground">({reviews.length} reviews)</span>
                    </div>
                    <p className="text-sm text-indigo-600 mb-3">
                      Profile completeness: {tutor.profile_completeness ?? 0}%
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {tutor.teaches_online && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Online Classes</Badge>}
                      {tutor.teaches_at_home && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Home Tuition</Badge>}
                      {tutor.can_travel && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Can Travel</Badge>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subjects & Classes */}
            {tutor.subjects && tutor.subjects.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Book className="w-5 h-5" />
                    Subjects & Classes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tutor.subjects.map((sub, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <span className="font-semibold break-words">{sub.subject}</span>
                          <div className="flex flex-wrap gap-1 md:justify-end md:max-w-[70%]">
                          {sub.classes?.map((cls, i) => (
                            <Badge key={i} variant="secondary">{cls}</Badge>
                          ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Intro Video For Students */}
            {getYouTubeEmbedUrl(tutor.intro_video_url) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Introduction Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <iframe
                      width="100%"
                      height="100%"
                      src={getYouTubeEmbedUrl(tutor.intro_video_url)}
                      title="Tutor Intro Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Teaching Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Teaching Preferences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {tutor.teaches_online && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Online</Badge>}
                  {tutor.teaches_at_home && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Tutor Home</Badge>}
                  {tutor.can_travel && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Student Home</Badge>}
                  {!tutor.teaches_online && !tutor.teaches_at_home && !tutor.can_travel && (
                    <span className="text-sm text-muted-foreground">Not specified</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Experience */}
            <Card>
              <CardHeader>
                <CardTitle>Experience</CardTitle>
              </CardHeader>
              <CardContent>
                {tutor.experience && tutor.experience.length > 0 ? (
                  <div className="space-y-4">
                    {tutor.experience.map((exp, idx) => (
                      <div key={idx}>
                        <h3 className="font-semibold break-words">{exp.role || 'Not specified'}</h3>
                        <p className="text-sm text-muted-foreground break-words">{exp.company_institute || 'Not specified'}</p>
                        <p className="text-sm text-muted-foreground break-words">{exp.duration || 'Not specified'}</p>
                        {idx < tutor.experience.length - 1 && <Separator className="mt-4" />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Experience details not added yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Qualifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Qualifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {qualificationItems.length > 0 ? (
                  <div className="space-y-3">
                    {qualificationItems.map((item) => (
                      <div key={item.label} className="p-3 border rounded-lg">
                        <p className="font-semibold">{item.label}</p>
                        <p className="text-sm text-muted-foreground break-words">{item.details}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Qualification details not added yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Reviews ({reviews.length})
                  </CardTitle>
                  <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
                    <DialogTrigger asChild>
                      <Button data-testid="write-review-btn" variant="outline" className="rounded-full">
                        {existingReview ? 'Edit Your Review' : 'Write a Review'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {existingReview ? `Edit Your Review for ${tutor.name}` : `Write a Review for ${tutor.name}`}
                        </DialogTitle>
                        <DialogDescription>
                          {existingReview 
                            ? 'Update your review (minimum 20 characters)' 
                            : 'Share your experience (minimum 20 characters)'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSubmitReview} className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Rating *</label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                data-testid={`rating-${star}-btn`}
                                onClick={() => setReviewData({ ...reviewData, rating: star })}
                                className={`text-3xl transition-colors ${
                                  star <= reviewData.rating ? 'text-yellow-500' : 'text-gray-300'
                                }`}
                              >
                                ⭐
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {reviewData.rating === 1 && 'Poor'}
                            {reviewData.rating === 2 && 'Below Average'}
                            {reviewData.rating === 3 && 'Average'}
                            {reviewData.rating === 4 && 'Good'}
                            {reviewData.rating === 5 && 'Excellent'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Your Review * (Minimum 20 characters)</label>
                          <Textarea
                            data-testid="review-comment-input"
                            value={reviewData.comment}
                            onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                            placeholder="Share your experience with this tutor. What did you like? How did they help you? (Min 20 characters)"
                            rows={4}
                            minLength={20}
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {reviewData.comment?.length || 0} / 20 characters minimum
                          </p>
                        </div>
                        <Button 
                          data-testid="submit-review-btn" 
                          type="submit" 
                          className="w-full rounded-full"
                          disabled={!reviewData.comment || reviewData.comment.length < 20}
                        >
                          {existingReview ? 'Update Review' : 'Submit Review'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No reviews yet</p>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{review.student_name}</p>
                            <div className="flex items-center gap-1">
                              {[...Array(review.rating)].map((_, i) => (
                                <span key={i} className="text-yellow-500">⭐</span>
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {review.comment && <p className="text-sm">{review.comment}</p>}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Contact & Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Tutor</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  data-testid="message-tutor-btn"
                  onClick={handleSendMessage}
                  className="w-full rounded-full flex items-center gap-2"
                  variant="outline"
                >
                  <MessageSquare className="w-4 h-4" />
                  {accessInfo.has_message_access ? 'Message' : 'Message (100 coins)'}
                </Button>
                {accessInfo.has_contact_access && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <p className="text-xs text-green-700">Tutor Contact Number</p>
                    <p className="text-base font-semibold text-green-900">{unlockedPhone || tutor?.mobile || 'Not provided'}</p>
                  </div>
                )}
                <Button
                  data-testid="unlock-phone-btn"
                  onClick={handleContactTutor}
                  className="w-full rounded-full flex items-center gap-2 bg-orange-500 hover:bg-orange-600"
                  disabled={accessInfo.has_contact_access}
                >
                  <Phone className="w-4 h-4" />
                  {accessInfo.has_contact_access ? 'Phone Unlocked' : 'Unlock Phone (100 coins)'}
                </Button>
                {user && user.role !== 'tutor' && (
                  <p className="text-xs text-muted-foreground text-center">
                    Your balance: {accessInfo.current_coins} coins
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Profile Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Fee Range</p>
                  <p className="font-semibold flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    ₹{tutor.fee_min || 0} - ₹{tutor.fee_max || 0}/hr
                  </p>
                </div>
                <Separator />
                {tutor.languages && tutor.languages.length > 0 && (
                  <>
                    <div>
                      <p className="text-muted-foreground flex items-center gap-1">
                        <Languages className="w-4 h-4" />
                        Languages
                      </p>
                      <p className="font-semibold">{tutor.languages.join(', ')}</p>
                    </div>
                    <Separator />
                  </>
                )}
                <div>
                  <p className="text-muted-foreground">Gender</p>
                  <p className="font-semibold">{tutor.gender || 'Not specified'}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-muted-foreground">Works as</p>
                  <p className="font-semibold">{tutor.works_as || 'Professional Tutor'}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-muted-foreground">Homework Help</p>
                  <p className="font-semibold">{tutor.homework_help ? 'Yes' : 'No'}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-muted-foreground">Registered</p>
                  <p className="font-semibold">
                    {new Date(tutor.registered_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Trust Signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4" />
                    Verified Contact
                  </span>
                  <Badge variant={tutor.contact_verified ? 'default' : 'outline'}>
                    {tutor.contact_verified ? 'Verified' : 'Not verified'}
                  </Badge>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Profile Completion</span>
                  <span className="font-semibold">{tutor.profile_completeness ?? 0}%</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reviews</span>
                  <span className="font-semibold">{tutor.reviews_count ?? reviews.length}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock3 className="w-4 h-4" />
                    Avg Response Time
                  </span>
                  <span className="font-semibold text-right">{formatResponseTime(tutor.avg_response_minutes)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
